import { emptyObject } from '@constants/empty';
import React, { useMemo, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { AnyfeedDataProps } from 'types/unacademyIcons/anyfeed.type';
import { mediaQuery, Spacings } from '@styles/index';
import { SPACING_4B, SPACING_8B } from '@styles/constants/spacings';
import AnyfeedButton from '@unicons-comp/AnyfeedButton/AnyfeedButton';
import UN_ICONS_TYPOGRAPHY_VARIANT from 'types/unacademyIcons/typography.type';
import Typography from '@unicons-base/Typography/Typography';
import useWindowSize from '@hooks/useWindowSize';
import Link from '@base/Link/Link';
import pushRoute from '@utils/pushRoute';
import { COURSE_PATHNAME } from '@constants/unacademyIcons/icons.constants';

const Wrapper = styled.div`
  position: relative;
  height: 640px;
  margin: 0 auto;
  border-radius: 8px;
  padding-bottom: ${SPACING_8B};
  padding-top: 0;
  background: none;
  ${mediaQuery.uptoTablet} {
    height: 100%;
    margin: 0px ${SPACING_4B};
    padding: 0px;
    max-width: 100%;
    justify-content: center;
    align-items: center;
    flex-direction: column-reverse;
    background: var(--color-i-icon-dark-primary);
  }
`;

const StyledButton = styled(AnyfeedButton)`
  margin: auto;
  margin-top: ${SPACING_8B};
  &:hover {
    box-shadow: none;
  }
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_6B};
    max-width: 296px;
  }
  ${mediaQuery.uptoMobile} {
    margin-left: 0;
    margin-right: 0;
    max-width: 100%;
  }
`;

const StyledTypography = styled(Typography)`
  text-align: center;
  max-width: 686px;
  margin: auto;
  ${mediaQuery.uptoMobile} {
    line-height: 1.5;
  }
`;

const StyledCover = styled.img`
  cursor: pointer;
  display: flex;
  justify-content: center;
  margin: auto;
  margin-bottom: ${Spacings.SPACING_6B};
`;

const StyledImage = styled.img<{ $isActive: boolean }>`
  position: ${({ $isActive }) => ($isActive ? 'relative' : 'absolute')};
  opacity: ${({ $isActive }) => ($isActive ? '1' : '0')};
  left: 0;
  top: 0;
  transition: opacity 1000ms ease-in;
  width: 100%;
  cursor: pointer;
  border-radius: 8px;
  ${mediaQuery.uptoMobile} {
    height: auto;
  }
`;

const StyledContent = styled.div`
  z-index: 2;
  position: absolute;
  bottom: ${Spacings.SPACING_14B};
  left: 0;
  right: 0;
  ${mediaQuery.uptoTablet} {
    position: relative;
    bottom: ${Spacings.SPACING_4B};
    margin-top: ${Spacings.SPACING_6B};
    padding: 0px ${SPACING_4B};
  }
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_8B};
  }
`;

const Overlay = styled.div`
  cursor: pointer;
  background: linear-gradient(
    179.56deg,
    rgba(0, 0, 0, 0) 38.03%,
    #000000 86.62%
  );
  position: absolute;
  width: 100%;
  height: 100%;
  bottom: 0;
  left: 0;
  right: 0;
  ${mediaQuery.uptoTablet} {
    display: none;
  }
`;

const StyledTitle = styled.span`
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent;
  background: linear-gradient(
    261.69deg,
    var(--color-i-icon-gradient-1) 0%,
    var(--color-i-icon-gradient-2) 69.95%,
    var(--color-i-icon-gradient-3) 100%
  );
`;

const AnyfeedCATCL: React.FC<AnyfeedDataProps> = ({ data, list }) => {
  const { cta, title: heading, carousel } = list?.[0] || emptyObject;
  const [currentImage, setCurrentImage] = useState(0);
  const { image } = carousel?.[0] || emptyObject;
  const { metadata, backgroundColors, titleColors } = cta || emptyObject;

  const { isMobile } = useWindowSize();

  const titleContent = useMemo(() => {
    return <StyledTitle>{cta?.title}</StyledTitle>;
  }, [cta?.title]);

  // TODO: Complete the transition logic

  useEffect(() => {
    const timer = setInterval(() => {
      if (currentImage >= carousel.length - 1) {
        setCurrentImage(0);
      } else {
        setCurrentImage(currentImage + 1);
      }
    }, 4000);

    return () => {
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentImage]);

  const navigateToCourse = () => {
    pushRoute(COURSE_PATHNAME, `/icons/courses/${metadata?.slug}`, {
      shallow: true
    });
  };

  return (
    <Wrapper>
      {carousel.map((carouselImage, index) => (
        <React.Fragment key={carouselImage.image}>
          <StyledImage
            src={carouselImage.image || image}
            alt="Unacademy Icons - Sachin Tendulkar"
            $isActive={currentImage === index}
          />
        </React.Fragment>
      ))}

      <StyledContent>
        <StyledCover
          src={heading?.titleImage}
          alt="Cricket with Sachin"
          width={isMobile ? 300 : 416}
          height={isMobile ? 60 : 80}
          onClick={navigateToCourse}
        />
        {heading?.description && (
          <StyledTypography
            variant={UN_ICONS_TYPOGRAPHY_VARIANT.SUBTITLE2}
            color={
              heading?.descriptionColor || 'var(--color-i-icon-dark-secondary)'
            }
          >
            {heading.description}
          </StyledTypography>
        )}
        {/* Feature List Component */}
        {cta && (
          <Link
            href="/icons/courses/[courseSlug]"
            as={`/icons/courses/${metadata?.slug}`}
          >
            <StyledButton
              title={titleContent}
              titleColor={cta?.titleColor}
              titleColors={titleColors}
              backgroundColors={backgroundColors}
              isSubscriptionCta={false}
            />
          </Link>
        )}
      </StyledContent>
      <Overlay onClick={navigateToCourse} />
    </Wrapper>
  );
};

AnyfeedCATCL.displayName = 'Anyfeed CATCL';

export default AnyfeedCATCL;
